/* Third-party app accessibility shim [OOB-509]
 *
 * Patches DOM injected at runtime by third-party apps whose markup the theme
 * cannot edit at the source. Each patch is guarded so it never overwrites an
 * attribute the app starts setting itself — if a vendor ships a proper fix,
 * the shim becomes a no-op for that node.
 *
 * Slide Cart HQ was uninstalled on 2026-09-30 and its patches below are
 * commented out (kept for a reinstall). The header cart count handling at the
 * bottom is still live: the theme's own /cart page relies on it.
 *
 * Previously covered:
 *   - Slide Cart HQ (cart drawer, present on every page):
 *     .slidecarthq-overlay is a role="button" div with no accessible name
 *     (axe: aria-command-name, serious); the drawer's <header> is scoped to
 *     <body> so it maps to a second banner landmark (landmark-no-duplicate-
 *     banner / landmark-unique); the drawer content sits outside any landmark
 *     (region); and the floating launcher a.floating-cart (a fixed-position
 *     <a> whose only content is the item-count bubble) has no accessible name
 *     beyond the bare number (WCAG 4.1.2). Verified against Slide Cart HQ
 *     data-version 1.17.0.
 *   - Slide Cart HQ focus management [OOB-527]: the app opens its dialog
 *     without moving focus into it, without trapping focus, and without
 *     making the background inert (WCAG 2.4.3 / 4.1.2). When the drawer
 *     opens this shim moves focus into the dialog, traps Tab within it,
 *     marks it aria-modal and inerts the background, and on close restores
 *     focus to the element that opened it. Every step is guarded so a future
 *     vendor fix wins (we never override an aria-modal/inert the app sets).
 *
 * Deliberately NOT covered — Escape-to-close on the Slide Cart HQ drawer
 * [OOB-541]. Escape *does* close the drawer, and testing it is expected; the
 * app handles it itself, so the theme adds nothing. Read the note beside the
 * drawer's keydown handler below before writing any Escape code here.
 *
 * Also here (see "Header cart count" further down): keeping the custom header
 * cart link's accessible name in sync and announcing count changes [OOB-519].
 * Slide Cart HQ updates the visible count but not the link's aria-label and
 * never announces it, so screen-reader users get a stale/silent header cart.
 *
 * Also live: the Yotpo Promoted Products carousel (#yotpo-promoted-products-
 * widget). Each card becomes a link described as opening a new tab; the rating
 * is one img, the price is labelled, and Glide clones are hidden. The Yotpo
 * UGC gallery title gets the aria-level its role="heading" requires.
 */
(function () {
  // ===========================================================================
  // DISABLED 2026-09-30: Slide Cart HQ was uninstalled (the store uses /cart).
  // Its patches and focus management below are commented out, not deleted, so
  // they can be restored if the app is ever reinstalled: uncomment this block
  // and the init() call in boot(). Last verified against Slide Cart HQ 1.17.0.
  // ===========================================================================
  // // Accessible names are sourced from the theme's localization bridge
  // // (window.accessibilityStrings, built in layout/theme.liquid) so non-English
  // // locales get translated names; the English literals are the fallback for
  // // when the bridge has not been populated.
  // function a11yString(key, fallback) {
  //   var strings = window.accessibilityStrings;
  //   return (strings && strings[key]) || fallback;
  // }
  //
  // // The shim silently overrides a third-party app's private markup, so make
  // // its health visible in the console — it is intentionally noisy about
  // // working (so it is never forgotten) and about breaking (so a developer
  // // eventually notices if a Slide Cart HQ update moves the markup out from
  // // under our selectors).
  // var LOG_PREFIX = '[a11y-shim] Slide Cart HQ';
  // var loggedSuccess = false;
  // var warnedDrift = false;
  //
  // function patchSlideCart() {
  //   var root = document.getElementById('slidecarthq');
  //   if (!root) return;
  //
  //   // Guard each attribute independently so a partial vendor fix still wins:
  //   // skip the name only when the node already has an aria-label OR
  //   // aria-labelledby (either provides an accessible name).
  //   var overlay = root.querySelector('.slidecarthq-overlay');
  //   if (overlay && !overlay.getAttribute('aria-label') && !overlay.getAttribute('aria-labelledby')) {
  //     overlay.setAttribute('aria-label', a11yString('closeCart', 'Close cart'));
  //   }
  //
  //   // The floating launcher is an <a> whose only content is the item-count
  //   // bubble, so a screen reader announces just the number with no hint that
  //   // it opens the cart. Name it (the bubble's count stays visible, and the
  //   // header cart link already announces the live count). The launcher is a
  //   // Slide Cart HQ setting and may be absent, so this guard is independent of
  //   // the success check below.
  //   var trigger = root.querySelector('a.floating-cart');
  //   if (trigger && !trigger.getAttribute('aria-label') && !trigger.getAttribute('aria-labelledby')) {
  //     trigger.setAttribute('aria-label', a11yString('viewCart', 'View cart'));
  //   }
  //
  //   var header = root.querySelector('header[data-testid="Header"]');
  //   if (header && !header.getAttribute('role')) {
  //     header.setAttribute('role', 'presentation');
  //   }
  //
  //   // The panel is normally a descendant, but match the root itself too in
  //   // case a future release makes #slidecarthq the panel element.
  //   // aria-modal/inert are NOT set here: the drawer stays in the DOM (and the
  //   // accessibility tree) while closed, so a permanently-modal dialog would
  //   // mark the rest of the page inert for screen readers. They are applied
  //   // only while the drawer is open — see the focus-management module below.
  //   var panel = root.matches('div.slidecarthq') ? root : root.querySelector('div.slidecarthq');
  //   if (panel) {
  //     if (!panel.getAttribute('role')) {
  //       panel.setAttribute('role', 'dialog');
  //     }
  //     if (!panel.getAttribute('aria-label') && !panel.getAttribute('aria-labelledby')) {
  //       panel.setAttribute('aria-label', a11yString('cartTitle', 'Shopping cart'));
  //     }
  //   }
  //
  //   // Once the drawer has rendered we expect all three nodes present together
  //   // (the drawer stays in the DOM while closed). Log success a single time so
  //   // the shim's presence is always visible in the console.
  //   if (!loggedSuccess && overlay && header && panel) {
  //     loggedSuccess = true;
  //     console.log(LOG_PREFIX + ': accessibility attributes applied (close button, header, dialog).');
  //   }
  // }
  //
  // // ---------------------------------------------------------------------------
  // // Focus management for the Slide Cart HQ dialog [OOB-527]
  // //
  // // The app opens the drawer but leaves focus on <body>, never traps focus, and
  // // never inerts the background. We layer modal-dialog semantics on top of the
  // // app's markup: on open, move focus in + trap Tab + aria-modal + inert
  // // background; on close, undo all of it and restore focus to the opener.
  // // The open/close edge is driven by two independent signals — the vendor's
  // // `open` class and on-screen geometry — that must agree (computeOpenState),
  // // so vendor drift disables this module instead of misfiring it.
  // // ---------------------------------------------------------------------------
  //
  // var FOCUSABLE_SELECTOR = [
  //   'a[href]',
  //   'button:not([disabled])',
  //   'input:not([disabled]):not([type="hidden"])',
  //   'select:not([disabled])',
  //   'textarea:not([disabled])',
  //   '[tabindex]:not([tabindex="-1"])'
  // ].join(',');
  //
  // var drawerOpen = false;       // last known open state (drives open/close edges)
  // var openerEl = null;          // element to restore focus to on close
  // var pendingTrigger = null;    // last control activated while the drawer was closed
  // var managedModal = false;     // we (not the vendor) set aria-modal on the panel
  // var managedTabindex = false;  // we (not the vendor) set tabindex on the panel
  // var inertedNodes = [];        // background nodes we marked inert/aria-hidden
  //
  // // "Open" means actually rendered and on-screen. The panel stays in the DOM
  // // while closed (display:none or translated off-canvas), so test computed
  // // visibility AND that the box intersects the viewport — a transform-based
  // // slide-out keeps a non-zero box but pushes it past the viewport edge.
  // function isElementOpen(el) {
  //   if (!el || !el.isConnected) return false;
  //   var style = window.getComputedStyle(el);
  //   if (style.display === 'none' || style.visibility === 'hidden') return false;
  //   if (parseFloat(style.opacity) === 0) return false;
  //   var rect = el.getBoundingClientRect();
  //   if (rect.width === 0 || rect.height === 0) return false;
  //   // Compare against the layout viewport (documentElement.clientWidth/Height),
  //   // not window.innerWidth/Height: fixed elements are laid out relative to the
  //   // layout viewport, which excludes classic scrollbars. With a visible
  //   // scrollbar the closed panel's left edge lands at clientWidth — several px
  //   // inside innerWidth — and an innerWidth comparison misreads it as open.
  //   var viewportWidth = document.documentElement.clientWidth;
  //   var viewportHeight = document.documentElement.clientHeight;
  //   if (rect.right <= 0 || rect.left >= viewportWidth) return false;
  //   if (rect.bottom <= 0 || rect.top >= viewportHeight) return false;
  //   return true;
  // }
  //
  // // The vendor marks the panel with an `open` class while the drawer is shown
  // // (verified on v1.17.0); the geometry check above confirms it on-screen. The
  // // modal side effects only fire while BOTH agree: a false "open" is far more
  // // destructive (inert page, stolen focus) than a false "closed" (no focus
  // // management on a working page), so if the vendor renames the class,
  // // restyles the slide animation, or the geometry check breaks, the module
  // // stands down instead of guessing. Because it takes both signals to open but
  // // losing either closes, a signal that sticks "open" can degrade the trap yet
  // // can never leave the page inert. The signals legitimately disagree for the
  // // few hundred ms of the slide animation (the class flips first, the box
  // // catches up), so a disagreement schedules re-checks and only warns once it
  // // has persisted well past any animation.
  // var openSignalTimer = null;     // pending re-check while the signals disagree
  // var openSignalsDisagreeAt = 0;  // when the current disagreement started
  // var warnedOpenDrift = false;
  //
  // function computeOpenState(panel) {
  //   var classOpen = panel.classList.contains('open');
  //   var geometryOpen = isElementOpen(panel);
  //
  //   if (classOpen === geometryOpen) {
  //     openSignalsDisagreeAt = 0;
  //     // Signals agree again — clear the drift latch so a later transient
  //     // disagreement is re-checked (and, if genuinely persistent, re-warned)
  //     // instead of the module staying permanently stood-down after one blip.
  //     warnedOpenDrift = false;
  //     if (openSignalTimer) {
  //       clearTimeout(openSignalTimer);
  //       openSignalTimer = null;
  //     }
  //     return classOpen;
  //   }
  //
  //   if (!openSignalsDisagreeAt) openSignalsDisagreeAt = Date.now();
  //   if (Date.now() - openSignalsDisagreeAt > 2000 && !warnedOpenDrift) {
  //     warnedOpenDrift = true;
  //     console.warn(
  //       LOG_PREFIX + ': drawer open-state signals disagree (class "open": ' +
  //       classOpen + ', on-screen geometry: ' + geometryOpen + ') — its markup' +
  //       ' or styles may have changed, so focus management is standing down.' +
  //       ' Re-verify the selectors in assets/third-party-a11y-shim.js.'
  //     );
  //   } else if (!openSignalTimer && !warnedOpenDrift) {
  //     openSignalTimer = setTimeout(function () {
  //       openSignalTimer = null;
  //       syncDrawerState();
  //     }, 700);
  //   }
  //   return false;
  // }
  //
  // function getFocusable(container) {
  //   // Reuse the theme's canonical focusable-element query (assets/global.js,
  //   // loaded before this script) so there is a single definition of "what is
  //   // focusable"; fall back to the local selector if that global is ever
  //   // unavailable. The visibility filter is ours — it drops the off-screen
  //   // panel's controls before it animates in, which global.js does not do.
  //   var candidates = (typeof getFocusableElements === 'function')
  //     ? getFocusableElements(container)
  //     : Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE_SELECTOR));
  //   return candidates.filter(function (el) {
  //     return el === document.activeElement ||
  //       el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
  //   });
  // }
  //
  // // Make everything except the cart inert so AT can't escape the dialog. Walk
  // // DOWN from <body> through the ancestors of the two subtrees that must stay
  // // live — the cart itself and the OOB-519 cart-count announcer — inerting each
  // // sibling that holds neither. Descending (rather than only inerting direct
  // // <body> children and skipping any whole subtree that merely CONTAINS a live
  // // region) closes two gaps: the announcer lives deep inside the body-level
  // // header section, so the old body-child-granularity skip left the entire
  // // header (nav, search, account, cart link) interactive behind the "modal";
  // // and a cart root mounted inside a wrapper rather than directly under <body>
  // // is now isolated too.
  // function applyBackgroundInert(root) {
  //   inertedNodes = [];
  //   var announcer = document.getElementById('cart-count-announcer');
  //   (function inertSiblingsExcept(parent) {
  //     Array.prototype.forEach.call(parent.children, function (node) {
  //       // The exempt subtree roots stay fully interactive.
  //       if (node === root || node === announcer) return;
  //       // An ancestor of an exempt node: don't inert it — descend and inert its
  //       // other children, so only the exempt path is left live.
  //       if ((root && node.contains(root)) ||
  //           (announcer && node.contains(announcer))) {
  //         inertSiblingsExcept(node);
  //         return;
  //       }
  //       if (node.hasAttribute('aria-hidden')) return; // leave the app's own choices alone
  //       node.setAttribute('aria-hidden', 'true');
  //       try { node.inert = true; } catch (e) { /* inert unsupported — aria-hidden still applies */ }
  //       inertedNodes.push(node);
  //     });
  //   })(document.body);
  // }
  //
  // function removeBackgroundInert() {
  //   inertedNodes.forEach(function (node) {
  //     node.removeAttribute('aria-hidden');
  //     try { node.inert = false; } catch (e) { /* no-op */ }
  //   });
  //   inertedNodes = [];
  // }
  //
  // function trapFocus(panel, event) {
  //   if (event.key !== 'Tab') return;
  //   var focusables = getFocusable(panel);
  //   if (!focusables.length) {
  //     event.preventDefault();
  //     panel.focus();
  //     return;
  //   }
  //   var first = focusables[0];
  //   var last = focusables[focusables.length - 1];
  //   var active = document.activeElement;
  //   if (event.shiftKey) {
  //     // `active === panel` covers the just-opened state: the dialog container
  //     // itself is focused (tabindex=-1, so it is NOT in `focusables`), and a
  //     // first Shift+Tab must wrap to the last control rather than fall through
  //     // to the browser's default backward tab out of the trapped region.
  //     if (active === first || active === panel || !panel.contains(active)) {
  //       event.preventDefault();
  //       last.focus();
  //     }
  //   } else if (active === last || !panel.contains(active)) {
  //     event.preventDefault();
  //     first.focus();
  //   }
  // }
  //
  // var keydownHandler = null;
  //
  // function onDrawerOpen(panel) {
  //   openerEl = pendingTrigger;
  //   // Consume it: a later open that arrives without a fresh opening click (an
  //   // auto-open after an async add, a cross-tab cart sync) must not restore
  //   // focus to a stale, unrelated control on close.
  //   pendingTrigger = null;
  //
  //   // Only set aria-modal if the app hasn't — a vendor fix must win.
  //   if (!panel.getAttribute('aria-modal')) {
  //     panel.setAttribute('aria-modal', 'true');
  //     managedModal = true;
  //   }
  //
  //   applyBackgroundInert(document.getElementById('slidecarthq'));
  //
  //   // Focus the dialog itself so its name + role ("Your cart, dialog") are
  //   // announced; a tabindex of -1 makes it programmatically focusable without
  //   // adding it to the tab order. Track that we set it so close can remove it.
  //   if (!panel.hasAttribute('tabindex')) {
  //     panel.setAttribute('tabindex', '-1');
  //     managedTabindex = true;
  //   }
  //   panel.focus();
  //
  //   // Tab only. Escape is handled by the app, not the theme [OOB-541].
  //   //
  //   // EXPECTED BEHAVIOUR — TEST IT: with the drawer open, Escape closes it.
  //   // The WAI-ARIA APG dialog pattern requires it, and because this module
  //   // presents the drawer as role="dialog" + aria-modal with focus trapped
  //   // inside, Escape is the dismissal a keyboard or screen-reader user will
  //   // reach for first. Include it in every keyboard/a11y pass over the cart
  //   // (open the drawer, press Escape, expect it to close and focus to return
  //   // to the trigger). Finding no Escape code in this file is NOT a reason to
  //   // skip the check or to add a handler.
  //   //
  //   // It works because SLIDE CART HQ ships its own handler, as of extension
  //   // build slide-cart-1-90 — its dialog keydown calls CartStore.closeCart():
  //   //
  //   //   this.handleDialogKeyDown = e => { const t = this.props.CartStore;
  //   //     t.open && ("Escape" !== e.key ? "Tab" === e.key && ... : t.closeCart()) }
  //   //
  //   // Verified live on oobli.com 2026-07-29 (app data-version 1.17.0): Escape
  //   // closes the drawer with zero theme code involved.
  //   //
  //   // The theme deliberately ships NO Escape fallback. One was built and
  //   // rejected on OOB-541 (PR #142). A theme-side handler cannot detect the
  //   // app's listener, so it has to *infer* whether the app already closed the
  //   // drawer; inferring wrong closes it twice, and a second close on a toggle
  //   // control re-opens it. Insuring against a hypothetical vendor regression
  //   // wasn't worth that risk plus a code path that never executes on a current
  //   // app build and would rot unnoticed.
  //   //
  //   // SO: if Escape ever stops closing the drawer, that is a VENDOR REGRESSION,
  //   // not a missing theme feature. Re-open OOB-541, raise it with Slide Cart
  //   // HQ, and only shim it here if the vendor won't fix it.
  //   keydownHandler = function (event) { trapFocus(panel, event); };
  //   document.addEventListener('keydown', keydownHandler, true);
  // }
  //
  // function onDrawerClose(panel) {
  //   if (keydownHandler) {
  //     document.removeEventListener('keydown', keydownHandler, true);
  //     keydownHandler = null;
  //   }
  //
  //   removeBackgroundInert();
  //
  //   // Clear the managed flags even when panel is null (drawer torn down while
  //   // open): a stale managedModal=true would otherwise let a later close strip
  //   // an aria-modal that a FUTURE vendor release sets itself, breaking the
  //   // "vendor fix wins" guarantee. Same reasoning for the tabindex we added.
  //   if (managedModal) {
  //     if (panel) panel.removeAttribute('aria-modal');
  //     managedModal = false;
  //   }
  //   if (managedTabindex) {
  //     if (panel) panel.removeAttribute('tabindex');
  //     managedTabindex = false;
  //   }
  //
  //   // Restore focus to the opener, but only if focus is still inside the drawer
  //   // or on <body> — never yank focus away if the user has since moved it
  //   // somewhere intentional.
  //   var active = document.activeElement;
  //   var focusStuck = !active || active === document.body ||
  //     (panel && panel.contains(active));
  //   if (focusStuck && openerEl && openerEl.isConnected &&
  //       typeof openerEl.focus === 'function') {
  //     openerEl.focus();
  //   }
  //   openerEl = null;
  // }
  //
  // // Compares live open state against drawerOpen and fires the open/close edge.
  // function syncDrawerState() {
  //   var root = document.getElementById('slidecarthq');
  //   if (!root) return;
  //   var panel = root.matches('div.slidecarthq') ? root : root.querySelector('div.slidecarthq');
  //   if (!panel) return;
  //
  //   var open = computeOpenState(panel);
  //   if (open === drawerOpen) return;
  //   drawerOpen = open;
  //   if (open) {
  //     onDrawerOpen(panel);
  //   } else {
  //     onDrawerClose(panel);
  //   }
  // }
  //
  // // Record the control that opens the drawer so focus can be returned to it.
  // // Enter on a button fires a click too, so this captures both pointer and
  // // keyboard activation. Only track while closed so internal drawer clicks (the
  // // close button, steppers) don't clobber the original opener. After an open
  // // gesture the drawer animates in over a few hundred ms, so re-check then in
  // // case the app toggles open via markup the MutationObserver can't see.
  // function onDocumentClick(event) {
  //   if (!drawerOpen) {
  //     var target = event.target.closest &&
  //       event.target.closest('button, a[href], [role="button"], input[type="submit"]');
  //     if (target) pendingTrigger = target;
  //   }
  //   setTimeout(syncDrawerState, 350);
  //   setTimeout(syncDrawerState, 800);
  // }
  //
  // function init() {
  //   document.addEventListener('click', onDocumentClick, true);
  //
  //   var cartObserver = null;
  //   var bodyObserver = null;
  //   var giveUpTimer = null;
  //   var healthCheckScheduled = false;
  //
  //   // Scheduled once, after the cart root first appears. By the time it fires
  //   // the app has had time to finish its async render; if the patch still
  //   // never completed (loggedSuccess never flipped) while the root is present,
  //   // Slide Cart HQ has likely changed its markup and the shim needs updating.
  //   function scheduleHealthCheck() {
  //     if (healthCheckScheduled) return;
  //     healthCheckScheduled = true;
  //     setTimeout(function () {
  //       if (loggedSuccess || warnedDrift) return;
  //       if (!document.getElementById('slidecarthq')) return; // no cart here — nothing to patch
  //       warnedDrift = true;
  //       console.warn(
  //         LOG_PREFIX + ': cart mounted but expected nodes were not found — its' +
  //         ' markup may have changed. Re-verify the selectors in' +
  //         ' assets/third-party-a11y-shim.js.'
  //       );
  //     }, 8000);
  //   }
  //
  //   function stopBodyObserver() {
  //     if (bodyObserver) {
  //       bodyObserver.disconnect();
  //       bodyObserver = null;
  //     }
  //     if (giveUpTimer) {
  //       clearTimeout(giveUpTimer);
  //       giveUpTimer = null;
  //     }
  //   }
  //
  //   // The app mounts asynchronously and re-renders the drawer on cart
  //   // changes, replacing patched nodes — watch its subtree and re-apply.
  //   function onCartMutation() {
  //     var root = document.getElementById('slidecarthq');
  //     // If the app removed/replaced the cart root, our observer is bound to a
  //     // detached node and will never recover — tear it down and re-discover.
  //     if (!root || !root.isConnected) {
  //       if (cartObserver) {
  //         cartObserver.disconnect();
  //         cartObserver = null;
  //       }
  //       // If the drawer node was torn down while open, unwind the modal state
  //       // so the background isn't left inert and focus isn't stranded.
  //       if (drawerOpen) {
  //         drawerOpen = false;
  //         onDrawerClose(null);
  //       }
  //       armBodyObserver();
  //       return;
  //     }
  //     patchSlideCart();
  //     syncDrawerState();
  //   }
  //
  //   function observeCartRoot() {
  //     var root = document.getElementById('slidecarthq');
  //     if (!root) return false;
  //     if (!cartObserver) {
  //       cartObserver = new MutationObserver(onCartMutation);
  //       // attributes:true so a class/hidden/open toggle on the panel (how the
  //       // app opens/closes the drawer) re-runs syncDrawerState, not just
  //       // child-list changes. `style` is intentionally NOT watched: the vendor
  //       // animates the slide via inline style, and watching it re-ran
  //       // syncDrawerState (a getComputedStyle + getBoundingClientRect reflow)
  //       // on every animation frame. The `.open` class is the signal we key on,
  //       // so the settled open/close state is still caught on the class toggle.
  //       cartObserver.observe(root, {
  //         childList: true,
  //         subtree: true,
  //         attributes: true,
  //         attributeFilter: ['class', 'hidden', 'aria-hidden', 'open']
  //       });
  //     }
  //     scheduleHealthCheck();
  //     patchSlideCart();
  //     syncDrawerState();
  //     return true;
  //   }
  //
  //   function armBodyObserver() {
  //     if (observeCartRoot()) return;
  //     if (bodyObserver) return;
  //     bodyObserver = new MutationObserver(function () {
  //       if (observeCartRoot()) stopBodyObserver();
  //     });
  //     bodyObserver.observe(document.body, { childList: true, subtree: true });
  //     // Bounded give-up: on a cart-less page the root never mounts, so don't
  //     // leave a whole-document observer running for the life of the page.
  //     // Trade-off: if the app mounts later than this window (well beyond the
  //     // sub-second mount observed for Slide Cart HQ v1.17.0) the drawer won't
  //     // be patched — re-arm on a cart-open interaction if that ever regresses.
  //     giveUpTimer = setTimeout(stopBodyObserver, 10000);
  //   }
  //
  //   armBodyObserver();
  // }
  // ===== END DISABLED Slide Cart HQ block ====================================

  // ---------------------------------------------------------------------------
  // Header cart count — accessible name + screen-reader announcement [OOB-519]
  //
  // The custom header cart link (#header-cart) shows a .floating-cart-bubble
  // count. The visible number is kept current by Slide Cart HQ's cart sync (and,
  // on the /cart page, by the theme's cart-page.js), but neither updates the
  // link's ACCESSIBLE NAME nor announces the change — so a screen reader reads a
  // stale "Cart, 0 items" after an add and the user gets no feedback. We patch
  // both here, off one signal: observing the bubble. This lives in the shim
  // (not the cart JS) because the gap is created by the third-party app, and the
  // shim runs on every cart-bearing page regardless of the dormant theme cart.
  //   - WCAG 4.1.2 (Name, Role, Value): keep #header-cart's aria-label in sync.
  //   - WCAG 4.1.3 (Status Messages): write the count into the polite live region
  //     (#cart-count-announcer) so it's announced without moving focus.
  // Note: changing aria-label alone is silent to AT — the live region is what
  // actually announces. Both are needed.
  function readCartCount(bubble) {
    if (!bubble) return null;
    // Read the visible TEXT only — not the `hidden` attribute, not
    // data-cart-count. Verified against live Slide Cart HQ (2026-06-24): on
    // add-to-cart the app sets the bubble's textContent ("1") but leaves the
    // server-rendered `hidden` attribute in place (CSS forces the bubble visible
    // with display:flex, so `hidden` is a dead no-op the app never clears), and
    // it never updates data-cart-count. textContent is the ONLY signal the live
    // cart keeps current — trusting `hidden` here made us read a full cart as
    // empty, leaving the aria-label stuck at "Cart, 0 items" with no
    // announcement. An empty bubble (no text) is the genuine empty-cart state.
    // Strip to digits only so a grouped count ("1,000") or stray whitespace
    // parses correctly rather than truncating at the separator.
    var text = (bubble.textContent || '').replace(/[^0-9]/g, '');
    if (text === '') return 0;
    var n = parseInt(text, 10);
    return isNaN(n) ? null : n;
  }

  // Built from the theme's localization bridge (window.cartStrings, set in
  // layout/theme.liquid) so plural rules and translations stay correct — no
  // hardcoded English.
  function cartCountLabel(count) {
    var s = window.cartStrings;
    if (!s || !s.cartLabel) return null;
    var phrase = null;
    if (s.cartCount) {
      phrase = count === 1
        ? s.cartCount.one
        : (s.cartCount.other || '').replace('[count]', count);
    }
    return phrase ? s.cartLabel + ', ' + phrase : s.cartLabel;
  }

  // Announce a message through the dedicated #cart-count-announcer live region.
  // Tuned for VoiceOver/Safari, which silently drops this announcement under the
  // default polite/short-delay setup (sources: Adrian Roselli 2026 live-region
  // testing, TPGi, NVDA #14591, TetraLogical, MDN):
  //  - ASSERTIVE: Slide Cart HQ resolves the add ~700ms after the click and opens
  //    a dialog, so by announce time the interaction is "over" and VoiceOver drops
  //    a low-priority POLITE update. The region is role="status" + aria-live=
  //    "assertive" (NOT role="alert" + aria-live together — that double-speaks on
  //    iOS VoiceOver) so it interrupts and is spoken.
  //  - DELAY ~200ms before writing so it lands AFTER the drawer's own a11y-tree
  //    churn instead of colliding with it (60ms collided with the dialog mount).
  //  - aria-atomic="true" + textContent: the whole, pre-composed string is read on
  //    each change.
  //  - TOGGLE a trailing no-break space so a repeated identical message still reads
  //    as a change (screen readers suppress byte-identical live-region updates).
  var announceToggle = false;
  var announceTimer = null;
  function announceCartMessage(message) {
    var region = document.getElementById('cart-count-announcer');
    if (!region) return;
    announceToggle = !announceToggle;
    var unique = announceToggle ? message + '\u00A0' : message;
    region.textContent = '';
    // Debounce: if several cart changes land within the delay (rapid adds), cancel
    // the pending write so only the latest, cumulative message is spoken instead of
    // a burst of announcements.
    if (announceTimer) window.clearTimeout(announceTimer);
    announceTimer = window.setTimeout(function () { region.textContent = unique; }, 200);
  }

  function initHeaderCartA11y() {
    var headerCart = document.getElementById('header-cart');
    // Scope to the bubble INSIDE the header link: Slide Cart HQ injects its own
    // .floating-cart-bubble elsewhere on the page, so a global querySelector can
    // bind to the wrong node. (Slide Cart HQ keeps both in sync, but this stays
    // unambiguous and matches the element our MutationObserver watches below.)
    var bubble = headerCart && headerCart.querySelector('.floating-cart-bubble');
    if (!headerCart || !bubble) return; // not the custom header layout
    if (headerCart.dataset.a11yCartBound) return; // already bound (e.g. re-init)
    headerCart.dataset.a11yCartBound = 'true';

    var lastCount = readCartCount(bubble);

    function sync(announce) {
      var count = readCartCount(bubble);
      if (count === null) return;
      var label = cartCountLabel(count);
      if (label) headerCart.setAttribute('aria-label', label);
      if (announce && count !== lastCount && label) {
        // On an INCREASE lead with the action confirmation ("Item added to your
        // cart") so the add is acknowledged, then the new cart state. On a
        // decrease (removal / qty down) just announce the resulting state — we
        // can't assert "added" and don't know the item name here to say what was
        // removed. itemAdded comes from the localization bridge (SHOP-008).
        // Note: count alone can't distinguish a true add from a quantity bump on
        // the /cart page, so a "+" there also reads "Item added…" — accepted, as
        // the count is the only signal this single observer has.
        var added = window.cartStrings && window.cartStrings.itemAdded;
        var message = (count > lastCount && added) ? added + '. ' + label : label;
        announceCartMessage(message);
      }
      lastCount = count;
    }

    // Fix the server-rendered name once, in case the bubble was already updated
    // (e.g. by Slide Cart HQ) before this ran. No announcement on load.
    sync(false);

    // Observe #header-cart's subtree so we catch the bubble's text changing and
    // the bubble being replaced wholesale.
    new MutationObserver(function () { sync(true); }).observe(headerCart, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cart-count'],
    });
  }

  var YOTPO_LOG_PREFIX = '[a11y-shim] Yotpo';
  var YOTPO_WIDGET_ID = 'yotpo-promoted-products-widget';
  var SHIM_MARKER = 'data-a11y-shim';
  var PRICE_LABEL_CLASS = 'a11y-shim-price-label';
  var NEW_TAB_CLASS = 'a11y-shim-new-tab';
  var CARD_ROLE = 'link';
  var newTabHintCount = 0;
  var yotpoLoggedSuccess = false;

  function setIfDifferent(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  // Glide clones slides after they are patched, copying the hint's id, so an id
  // that resolves to another card's hint is replaced.
  function describeAsNewTab(card) {
    var hint = card.querySelector('.' + NEW_TAB_CLASS);
    if (!hint) {
      hint = document.createElement('span');
      hint.className = 'visually-hidden ' + NEW_TAB_CLASS;
      hint.setAttribute('aria-hidden', 'true');
      hint.textContent = 'Opens in a new tab';
      card.appendChild(hint);
    }
    if (!hint.id || document.getElementById(hint.id) !== hint) {
      hint.id = 'a11y-shim-new-tab-' + (++newTabHintCount);
    }
    setIfDifferent(card, 'aria-describedby', hint.id);
  }

  // A card with a role the shim didn't set, or a real link or button, is a
  // vendor fix and is left alone.
  function patchPromotedCard(card) {
    var role = card.getAttribute('role');
    var ownedByShim = card.getAttribute(SHIM_MARKER) === 'yotpo';
    if (ownedByShim && role !== CARD_ROLE) {
      // Yotpo replaced the shim's role; its choice wins from here on.
      card.removeAttribute(SHIM_MARKER);
      return false;
    }
    if (!ownedByShim && role) return false;
    if (card.querySelector('a[href], button')) return false;

    var label = (card.getAttribute('aria-label') || '').trim();
    if (!label) return false;
    setIfDifferent(card, SHIM_MARKER, 'yotpo');
    setIfDifferent(card, 'role', CARD_ROLE);
    describeAsNewTab(card);

    // role="img" makes the block read once as Yotpo's own "5 out of 5 star
    // rating with 14 reviews". Chrome still exposes an img's text children, so
    // the stars and the bare "(14)" are hidden explicitly as well.
    var rating = card.querySelector('.pp-reviews-container');
    if (rating && rating.getAttribute('aria-label') && !rating.getAttribute('role')) {
      rating.setAttribute('role', 'img');
    }
    Array.prototype.forEach.call(card.querySelectorAll('.pp-star-ratings-container svg'), function (svg) {
      if (svg.hasAttribute('aria-hidden')) return;
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
    });
    var count = rating && rating.getAttribute('role') === 'img' &&
      rating.querySelector('.pp-product-total-reviews');
    if (count && !count.hasAttribute('aria-hidden')) {
      count.setAttribute('aria-hidden', 'true');
    }

    // A visually-hidden prefix, not aria-label: a <p> is generic too, so a
    // label on it would be ignored for the same reason the card's is.
    var price = card.querySelector('.pp-product-price');
    if (price && !price.querySelector('.' + PRICE_LABEL_CLASS) && !price.getAttribute('aria-label')) {
      var prefix = document.createElement('span');
      prefix.className = 'visually-hidden ' + PRICE_LABEL_CLASS;
      prefix.textContent = 'Price: ';
      price.insertBefore(prefix, price.firstChild);
    }
    return true;
  }

  function patchPromotedSlideVisibility(card) {
    var slide = card.closest('li.glide__slide');
    if (!slide) return;
    if (slide.classList.contains('glide__slide--clone')) {
      // Never hide a clone that holds focus or a tab stop: Glide briefly lands
      // on a clone when it wraps, and aria-hidden over focusable content
      // strands keyboard users (axe aria-hidden-focus).
      var holdsFocus = slide.contains(document.activeElement) ||
        !!slide.querySelector('[tabindex]:not([tabindex="-1"]), a[href], button');
      var hiddenByShim = slide.getAttribute(SHIM_MARKER) === 'yotpo-clone';
      if (holdsFocus) {
        if (hiddenByShim) {
          slide.removeAttribute('aria-hidden');
          slide.removeAttribute(SHIM_MARKER);
        }
      } else if (!slide.hasAttribute('aria-hidden')) {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute(SHIM_MARKER, 'yotpo-clone');
      }
    } else if (card.getAttribute('aria-hidden') === 'true') {
      // Yotpo's roving hide on a real slide. The card is never focusable while
      // hidden (Yotpo moves tabindex with it), so exposing it adds no tab stop.
      card.removeAttribute('aria-hidden');
    }
  }

  function patchYotpoPromoted() {
    var root = document.getElementById(YOTPO_WIDGET_ID);
    if (!root) return;
    var cards = root.querySelectorAll('.pp-card-container');
    var patched = 0;
    Array.prototype.forEach.call(cards, function (card) {
      if (!patchPromotedCard(card)) return;
      patchPromotedSlideVisibility(card);
      patched++;
    });
    if (!yotpoLoggedSuccess && patched) {
      yotpoLoggedSuccess = true;
      console.log(YOTPO_LOG_PREFIX + ': Promoted Products accessibility attributes applied (' +
        patched + ' cards: new-tab link, rating image, price context).');
    }
  }

  var widgetObserver = null;
  var parentObserver = null;  // notices the root itself being removed/replaced
  var observedRoot = null;
  var bodyObserver = null;
  var giveUpTimer = null;
  var patchTimer = null;
  var healthCheckScheduled = false;

  // The shim's own writes trigger the observer too; the patch only writes what
  // differs, so the follow-up run changes nothing and the loop ends.
  function schedulePatch() {
    if (patchTimer) clearTimeout(patchTimer);
    patchTimer = setTimeout(function () {
      patchTimer = null;
      var root = document.getElementById(YOTPO_WIDGET_ID);
      if (root !== observedRoot) {
        // Yotpo removed or swapped the widget root: the observers are bound
        // to a detached node, so drop them and re-discover.
        disconnectWidget();
        armBodyObserver();
        return;
      }
      patchYotpoPromoted();
    }, 50);
  }

  function disconnectWidget() {
    if (widgetObserver) widgetObserver.disconnect();
    if (parentObserver) parentObserver.disconnect();
    widgetObserver = null;
    parentObserver = null;
    observedRoot = null;
  }

  function scheduleHealthCheck() {
    if (healthCheckScheduled) return;
    healthCheckScheduled = true;
    setTimeout(function () {
      if (yotpoLoggedSuccess) return;
      var root = document.getElementById(YOTPO_WIDGET_ID);
      if (!root) return;
      console.warn(
        YOTPO_LOG_PREFIX + ': Promoted Products widget mounted but no cards were' +
        ' patched — its markup may have changed. Re-verify the selectors in' +
        ' assets/third-party-a11y-shim.js.'
      );
    }, 8000);
  }

  function stopBodyObserver() {
    if (bodyObserver) {
      bodyObserver.disconnect();
      bodyObserver = null;
    }
    if (giveUpTimer) {
      clearTimeout(giveUpTimer);
      giveUpTimer = null;
    }
  }

  function observeWidgetRoot() {
    var root = document.getElementById(YOTPO_WIDGET_ID);
    if (!root) return false;
    if (!widgetObserver) {
      observedRoot = root;
      // A subtree observer can't see its own root being removed; watching
      // the parent's children catches a removal or wholesale replacement.
      if (root.parentNode) {
        parentObserver = new MutationObserver(schedulePatch);
        parentObserver.observe(root.parentNode, { childList: true });
      }
      widgetObserver = new MutationObserver(schedulePatch);
      // Glide's slide changes move classes and Yotpo's roving aria-hidden /
      // tabindex; re-renders replace nodes. `style` is not watched — Glide
      // animates the track through it on every frame.
      widgetObserver.observe(root, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'tabindex', 'role', 'aria-label']
      });
    }
    scheduleHealthCheck();
    patchYotpoPromoted();
    return true;
  }

  // Yotpo mounts the widget client-side after load. Watch for it, but give up
  // on pages that never get one so no whole-document observer outlives it.
  function armBodyObserver() {
    if (observeWidgetRoot()) return;
    if (bodyObserver) return;
    bodyObserver = new MutationObserver(function () {
      if (observeWidgetRoot()) stopBodyObserver();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    giveUpTimer = setTimeout(stopBodyObserver, 15000);
  }


  // Safe to call repeatedly: it drops every Yotpo observer before re-arming.
  function initYotpoPromoted() {
    disconnectWidget();
    stopBodyObserver();
    armBodyObserver();
  }

  // Yotpo's UGC gallery title is a span with role="heading" but no aria-level (axe aria-required-attr).
  var UGC_TITLE_SELECTOR = '.yotpo-slider-title[role="heading"]';
  var ugcObservers = [];

  function patchUgcTitles(widget) {
    Array.prototype.forEach.call(widget.querySelectorAll(UGC_TITLE_SELECTOR), function (title) {
      if (!title.hasAttribute('aria-level')) title.setAttribute('aria-level', '2');
    });
  }

  function initYotpoUgc() {
    ugcObservers.forEach(function (observer) { observer.disconnect(); });
    ugcObservers = [];
    Array.prototype.forEach.call(document.querySelectorAll('.yotpo-pictures-widget'), function (widget) {
      patchUgcTitles(widget);
      var observer = new MutationObserver(function () { patchUgcTitles(widget); });
      observer.observe(widget, { childList: true, subtree: true });
      ugcObservers.push(observer);
    });
  }

  function runIsolated(name, fn) {
    try {
      fn();
    } catch (e) {
      console.warn('[a11y-shim] ' + name + ' failed', e);
    }
  }

  function initAll() {
    runIsolated('header cart', initHeaderCartA11y);
    runIsolated('Yotpo', initYotpoPromoted);
    runIsolated('Yotpo UGC', initYotpoUgc);
  }

  function boot() {
    // init(); // Slide Cart HQ patches: disabled, see the note above patchSlideCart.
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme-editor re-renders replace section DOM (ADR-SHOP-016). All three inits
  // are idempotent: a dataset guard for the header cart, a full re-arm for both
  // Yotpo patches.
  document.addEventListener('shopify:section:load', initAll);
})();
